import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CubeTransparentIcon, DocumentTextIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { FRAMEWORK_BY_ID, type FrameworkId } from '@/constants/frameworks.ts';
import type { GovernedRecommendationContent } from '@/pages/recommendationEngine/governedRecommendationTypes.ts';
import { formatConstraintSummary } from '@/utils/formatConstraintSummary.ts';

/** Shared recommendation body — same layout as step 1 STRATEGIC CASE READY. */
export function GovernedRecommendationSummary({ recommendation }: { recommendation: GovernedRecommendationContent }) {
  const { t } = useTranslation();
  const frameworkMeta = FRAMEWORK_BY_ID[recommendation.framework_id as FrameworkId];
  const constraintDisplay = formatConstraintSummary(recommendation.constraint_summary, (key) =>
    t(`commonIntakePage.clarify.${key}`),
  );

  return (
    <div className="rounded-2xl border border-[rgba(0,0,0,0.12)] bg-white p-4 flex flex-col gap-3">
      <SummaryRow
        icon={<DocumentTextIcon className="w-5 h-5 text-black/70 shrink-0" />}
        text={recommendation.issue_title}
      />
      <SummaryRow
        icon={<MagnifyingGlassIcon className="w-5 h-5 text-black/70 shrink-0" />}
        text={constraintDisplay}
      />
      <div className="flex flex-row items-center gap-2 font-dm text-sm text-black/80">
        <CubeTransparentIcon className="w-5 h-5 shrink-0" />
        <span>
          {t('recommendationEnginePage.ready.frameworkLabel')}{' '}
          <strong>{frameworkMeta?.label ?? recommendation.framework_id}</strong>
        </span>
      </div>
    </div>
  );
}

export function GovernedRecommendationSections({ recommendation }: { recommendation: GovernedRecommendationContent }) {
  const { t } = useTranslation();

  return (
    <>
      <RecommendationSection
        title={t('recommendationEnginePage.sections.recommendedDirection')}
        lead={recommendation.recommended_direction.lead}
        detail={recommendation.recommended_direction.detail}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.rootCause')}
        lead={recommendation.root_cause.lead}
        detail={recommendation.root_cause.detail}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.actionPlan')}
        lead={recommendation.action_plan.lead}
        bullets={recommendation.action_plan.bullets}
        bulletsLabel={t('recommendationEnginePage.sections.immediateActions')}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.expectedImpact')}
        bullets={recommendation.expected_impact}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.risksWatchouts')}
        bullets={recommendation.risks_watchouts}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.next30Days')}
        bullets={recommendation.next_30_days}
      />
      <p className="font-dm text-xs italic text-black/50 text-center px-2">
        {t('recommendationEnginePage.ready.frameworkFootnote')}
      </p>
    </>
  );
}

function SummaryRow({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex flex-row items-start gap-2">
      {icon}
      <p className="font-dm text-sm text-black/80 leading-relaxed">{text}</p>
    </div>
  );
}

function RecommendationSection({
  title,
  lead,
  detail,
  bullets,
  bulletsLabel,
}: {
  title: string;
  lead?: string;
  detail?: string;
  bullets?: string[];
  bulletsLabel?: string;
}) {
  return (
    <div className="rounded-2xl bg-[#F4F9FF] p-5 flex flex-col gap-2 font-dm text-sm text-black/70 leading-relaxed">
      <p className="font-semibold text-black/85">{title}</p>
      {lead && <p>{lead}</p>}
      {detail && <p className="text-black/60">{detail}</p>}
      {bullets && bullets.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {bulletsLabel && <p className="font-semibold text-black/75">{bulletsLabel}</p>}
          <ul className="list-disc pl-5 flex flex-col gap-1">
            {bullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
